import { useEffect, useMemo } from 'react'
import { App, Form, Space, Tag } from 'antd'
import { Edit } from '@refinedev/antd'
import { useOne } from '@refinedev/core'
import { Navigate, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { ProductDetailsRoot } from '@repo/sdk'
import { PageError } from '../../components/PageError'
import { getStoredUser } from '../../lib/auth-storage'
import { useProductImageUpload } from '../../shared/hooks/useProductImageUpload'
import { StatusBadge } from '../../components/StatusBadge'
import { useProductStatusConfig } from './productStatusConfig'
import { useProductEditSave } from './useProductEditSave'
import { usePublishProduct } from './usePublishProduct'
import { useArchiveProduct } from './useArchiveProduct'
import { useHideProduct } from './useHideProduct'
import { useUnhideProduct } from './useUnhideProduct'
import { useDeleteProduct } from './useDeleteProduct'
import { mapProductToFormValues } from './mapProductToFormValues'
import { ProductCreateForm } from './ProductCreateForm'
import { ProductEditHeaderButtons } from './ProductEditHeaderButtons'
import type { ProductCreateFormValues } from './ProductCreateFormTypes'
import { confirmPopup } from '@/components/popup'

type ProductStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SOLD_OUT'

export function ProductEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<ProductCreateFormValues>()
  const { upload, isUploading } = useProductImageUpload()
  const statusConfig = useProductStatusConfig()
  const { query } = useOne<ProductDetailsRoot>({
    resource: 'product-list',
    id: id ?? '',
    queryOptions: { enabled: !!id },
  })
  const product = query.data?.data
  const isBroadcast = product?.salesChannel === 'BROADCAST'
  const goToList = () => void navigate('/products')
  const { save, isPending: isSaving } = useProductEditSave(id ?? '', goToList)
  const { publishProduct, isPublishing } = usePublishProduct(() => void query.refetch())
  const { archiveProduct, isArchiving } = useArchiveProduct(() => void query.refetch())
  const { hideProduct, isHiding } = useHideProduct(() => void query.refetch())
  const { unhideProduct, isUnhiding } = useUnhideProduct(() => void query.refetch())
  const { deleteProduct, isDeleting } = useDeleteProduct(goToList)
  const isHidden = !!product?.hiddenAt
  const initialValues = useMemo(
    () => (product ? mapProductToFormValues(product) : undefined),
    [product],
  )

  useEffect(() => {
    if (isBroadcast) {
      message.warning(t('pages.products.messages.broadcastEditRestricted'))
    }
  }, [isBroadcast, message, t])

  const handleSubmit = () => {
    if (!product) return
    void form.validateFields().then((values) => save(values, product))
  }

  const handleCancel = () => {
    if (!form.isFieldsTouched()) return void navigate('/products')
    confirm(
      'common.modals.editCancelTitle',
      'common.modals.editCancelMessage',
      goToList,
      false,
      'product-edit-discard',
    )
  }

  const confirm = (
    titleKey: string,
    msgKey: string,
    action: () => void,
    danger = false,
    testId?: string,
  ) =>
    confirmPopup({
      title: t(titleKey),
      content: t(msgKey),
      okText: t(danger ? 'common.buttons.delete' : 'common.buttons.confirm'),
      okButtonProps: {
        ...(danger ? { danger: true } : {}),
        ...(testId ? { 'data-testid': `${testId}-confirm-btn` } : {}),
      },
      cancelButtonProps: testId ? { 'data-testid': `${testId}-cancel-btn` } : undefined,
      cancelText: t('common.buttons.cancel'),
      onOk: action,
    })

  const handlePublish = () =>
    id &&
    confirm(
      'common.modals.publishProductTitle',
      'common.modals.publishProductMessage',
      () => publishProduct(id),
      false,
      'product-edit-publish',
    )
  const handleArchive = () =>
    id &&
    confirm(
      'common.modals.archiveProductTitle',
      'common.modals.archiveProductMessage',
      () => archiveProduct(id),
      false,
      'product-edit-archive',
    )
  const handleHide = () =>
    id &&
    confirm(
      'common.modals.hideProductTitle',
      'common.modals.hideProductMessage',
      () => hideProduct(id),
      false,
      'product-edit-hide',
    )
  const handleUnhide = () =>
    id &&
    confirm(
      'common.modals.unhideProductTitle',
      'common.modals.unhideProductMessage',
      () => unhideProduct(id),
      false,
      'product-edit-unhide',
    )
  const handleDelete = () =>
    id &&
    confirm(
      'common.modals.deleteProductTitle',
      'common.modals.deleteProductMessage',
      () => deleteProduct(id),
      true,
      'product-edit-delete',
    )

  const isOwner = product?.dealerId === getStoredUser()?.userId

  if (query.isError) return <PageError onRetry={() => void query.refetch()} />

  if (query.isLoading || !product || !initialValues) {
    return <Edit isLoading title={t('pages.products.editTitle')} />
  }

  // Redirect declaratively: calling navigate() during render mutates the router mid-render.
  if (!isOwner || isBroadcast) {
    return <Navigate to="/products" replace />
  }

  return (
    <Edit
      title={
        <Space>
          {t('pages.products.editTitle')}
          <StatusBadge status={product.status as ProductStatus} configMap={statusConfig} />
          {isHidden && <Tag color="default">{t('common.status.hidden')}</Tag>}
        </Space>
      }
      headerButtons={
        <ProductEditHeaderButtons
          status={product.status}
          isHidden={isHidden}
          onPublish={handlePublish}
          onArchive={handleArchive}
          onHide={handleHide}
          onUnhide={handleUnhide}
          onDelete={handleDelete}
          onCancel={handleCancel}
          onSave={handleSubmit}
          isPublishing={isPublishing}
          isArchiving={isArchiving}
          isHiding={isHiding}
          isUnhiding={isUnhiding}
          isDeleting={isDeleting}
          isSaving={isSaving || isUploading}
        />
      }
      saveButtonProps={{ style: { display: 'none' } }}
    >
      <ProductCreateForm
        form={form}
        onImageUpload={upload}
        isUploading={isUploading}
        initialValues={initialValues}
        isEditMode
      />
    </Edit>
  )
}
